// ─────────────────────────────────────────────
//  MdCallEditControls — the per-row half of the MD Call rail's Edit mode.
//
//  Extracted from TimelineMdCallRail purely for size (that file lists the whole
//  operation and already carries the row builder). The GATE lives in the rail —
//  this renders one row's controls once the rail has decided the row is
//  editable, plus the named lock when it is not.
//
//  ⚠️ IT OWNS NO RULES. Departure is `DepartureFlow` via the page's
//  `renderDepart` (mandatory per-room reassignment + AIC handoff); naming the
//  AIC is the page's `onMakeAic`. Nothing here re-derives either.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import tokens from "../../theme/tokens";
import {
  mdCallLocationRole,
  mdCallCampusLabel,
  MD_CALL_LOCATION_LABELS,
} from "../../features/sheet-import/attendingCallList";
import { facilityLabel, shortFacilityLabel } from "../../domain/facilities";

const { color, font, radius } = tokens;

// ── WHICH CAMPUS IS THIS ROW WORKING AT ─────────────────────────────────────
//
// The fact the whole gate turns on. `mdCallBadgeColor` already answers it, but
// only as a COLOUR, and inverting a colour back to a facility id is a guess the
// moment two campuses share one (the fallback palette has six entries; a tenant
// may have more campuses than that).
//
// So the SAME matcher is asked a different way: `mdCallLocationRole` returns the
// facilityId of the campus whose named lead an entry matches, and handing it a
// ONE-ENTRY map asks "does this person match THIS name?". That reuses
// `personMatches` verbatim instead of copying its rules — copying them is
// exactly what produced the wrong-AIC badge found live on 2026-08-17 (a
// two-letter short code matched by containment), and a gate built on a second
// copy would hand edit rights over the wrong doctor.
//
// Campus order mirrors `campusOrder` inside attendingCallList (built-ins first
// in their seed order, then every other id sorted), so a doctor who appears on
// two boards resolves to the same campus that tinted their badge.
function campusIds(aicByFacility, doctorsByFacility) {
  const seen = new Set();
  for (const m of [aicByFacility, doctorsByFacility]) {
    for (const k of Object.keys(m || {})) if (k) seen.add(k);
  }
  const builtIn = Object.keys(MD_CALL_LOCATION_LABELS).filter((id) => seen.has(id));
  return [...builtIn, ...[...seen].filter((id) => !(id in MD_CALL_LOCATION_LABELS)).sort()];
}

export function railCampusOf(
  entry,
  { aicByFacility = {}, doctorsByFacility = {}, facilities = [] } = {}
) {
  for (const fid of campusIds(aicByFacility, doctorsByFacility)) {
    for (const n of [aicByFacility[fid], ...(doctorsByFacility[fid] || [])]) {
      if (n && mdCallLocationRole(entry, { [fid]: n }, facilities)) return fid;
    }
  }
  return null; // on no campus's board today — unknown, therefore not editable
}

// "AdventHealth Tampa" → "Tampa": the campus name alone, shortened the same way
// the topbar tag and mdCallCampusLabel shorten it, so the lock reason and the
// badge tooltip name the same thing.
export function campusName(fid, facilities) {
  const labels = (Array.isArray(facilities) ? facilities : [])
    .map((f) => f && (f.name || f.label))
    .filter(Boolean);
  return shortFacilityLabel(facilityLabel(fid, facilities), labels);
}

// What THIS campus calls the doctor leading it — "AIC" at Wesley/Carrollwood,
// "DOD" at Tampa. Editorial per campus and already owned by mdCallCampusLabel
// ("<campus> <title>"); the campus name is stripped back off rather than a
// second copy of the title map being kept here.
export function leadTitle(fid, facilities) {
  const full = mdCallCampusLabel(fid, facilities);
  const campus = campusName(fid, facilities);
  const rest = full.startsWith(campus) ? full.slice(campus.length).trim() : full;
  return rest || "AIC";
}

/**
 * @param row       a buildRailMdCall row
 * @param editable  the rail's gate said yes for THIS row
 * @param rowCampus the row's own campus id, or null when it is on no board
 * @param title     what the device's campus calls its lead ("AIC" / "DOD")
 */
export default function MdCallEditControls({
  row,
  editable,
  rowCampus,
  // Set only for an admin acting on a doctor who leads ANOTHER campus today.
  // A one-line fact beside live controls, not a warning: the admin chose this
  // row deliberately, and what they need to know is where the write lands.
  otherCampus = null,
  title = "AIC",
  facilities = [],
  onMakeAic = null,
  renderDepart = null,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!editable) {
    // Named, not greyed. This card lists other campuses ON PURPOSE, so
    // "untouchable" has to read as a rule and not as a fault — a silent grey
    // row on the one list whose job is showing the whole operation reads as a
    // broken app, and the reader's next move is to reload it.
    return (
      <span style={s.edit}>
        <span style={s.lockTag}>
          {rowCampus
            ? `🔒 ${campusName(rowCampus, facilities)} — edit from that campus`
            : "🔒 Not on today's boards — no edit here"}
        </span>
      </span>
    );
  }

  const crossNote = otherCampus ? (
    <span style={s.crossTag}>leads {otherCampus} today — this change applies here</span>
  ) : null;

  const makeAic = async () => {
    setBusy(true);
    setError("");
    try {
      await onMakeAic(row);
    } catch (err) {
      // ⚠️ VERBATIM, AND NEVER SWALLOWED. firestore.rules lets only the CURRENT
      // AIC or an administrator move `aicId` (the D-02 self-grab lock), so a
      // board manager who is not the AIC is refused BY THE SERVER after this
      // button looked perfectly enabled. Anything softer than what the refusal
      // said would leave them believing the handover happened.
      console.error("Make AIC failed:", err);
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={s.edit}>
      {row.isAIC ? (
        <span style={s.holdsTag}>Current {title}</span>
      ) : (
        onMakeAic && (
          <button
            type="button"
            style={s.aicBtn}
            disabled={busy}
            aria-label={`Make ${row.name} the ${title}`}
            onClick={makeAic}
          >
            {busy ? "Saving…" : `Make ${title}`}
          </button>
        )
      )}
      {renderDepart ? renderDepart(row) : null}
      {crossNote}
      {error && <span style={s.error} role="alert">{error}</span>}
    </span>
  );
}

const s = {
  // Controls get their OWN line, under the name. The rail's track is 179px
  // wide; anything beside a name overhangs the card, and that overhang is one
  // of the two reasons the ✕ was kept off this list in the first place.
  edit: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 2 },
  // 44px tall — read and pressed one-handed in a corridor.
  aicBtn: {
    minHeight: 44,
    padding: "0 8px",
    fontSize: 9.5,
    fontWeight: font.weight.bold,
    fontFamily: "inherit",
    color: color.accent.primary,
    background: "transparent",
    border: `1px solid ${color.accent.primary}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  // Same muted treatment as the lock tag: a statement of fact on a list whose
  // whole job is showing other campuses, never an alarm.
  crossTag: {
    fontSize: 8.5,
    color: color.text.muted,
    fontStyle: "italic",
    whiteSpace: "normal",
    flexBasis: "100%",
  },
  holdsTag: {
    fontSize: 8.5,
    fontWeight: font.weight.bold,
    letterSpacing: ".3px",
    color: color.text.muted,
    lineHeight: "44px",
  },
  // Muted, never red. A row belonging to another campus is the NORMAL state of
  // this list, not an alarm — red here would spend the colour that means
  // somebody is in trouble on "this one is Tampa's".
  lockTag: {
    fontSize: 8.5,
    color: color.text.muted,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    padding: "2px 5px",
    lineHeight: 1.3,
  },
  // surface.errorInk, not accent.error: accent.error as TEXT is 4.41:1 and
  // fails AA (see tokens.js) — it is only safe as a border or a fill.
  error: {
    fontSize: 9,
    color: color.surface.errorInk,
    lineHeight: 1.3,
    width: "100%",
  },
};
